import { useState } from 'react';
import { Search, Select } from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { LOG_TYPE } from '../activity-log-types';

/**
 * Contextual name filter, revealed once an Activity Type is picked:
 *  - Event → a searchable Select of the workspace's sent events
 *  - Attribute Change → a searchable Select of sent attributes
 *  - Profile Create / Delete → a free-text search
 *  - other types → nothing
 *
 * Deferred: object/relationship attribute-name options (per object-type) + the object-type dropdown.
 */

const FREE_TEXT_TYPES: string[] = [LOG_TYPE.ProfileCreate, LOG_TYPE.ProfileDelete];

function nameOptions(type: string, sentEvents: string[], sentAttributes: string[]): string[] {
  if (type === LOG_TYPE.Event) return sentEvents;
  if (type === LOG_TYPE.AttributeChange) return sentAttributes;
  return [];
}

function nameLabel(type: string): string {
  if (type === LOG_TYPE.Event) return 'Event name';
  if (type === LOG_TYPE.AttributeChange) return 'Attribute name';
  return 'Name';
}

/**
 * Free-text name search (Profile Create/Delete). Local input state, committed to the URL `name`
 * on a 1s debounce/clear (avoids a refetch per keystroke). The parent keys `<NameFilter>` on the
 * activity type, so this remounts whenever the type changes — resetting the seeded input and
 * letting Pluma's debounce cleanup cancel any pending `onSearch`, so a stale name can't be reapplied.
 */
type FreeTextNameFilterProps = { label: string; name: string; onChange: (n: string) => void };

function FreeTextNameFilter({ label, name, onChange }: FreeTextNameFilterProps) {
  const [value, setValue] = useState(name);
  return (
    <Search
      ariaLabel={label}
      placeholder="any"
      value={value}
      onSearchInput={setValue}
      onSearch={(next) => onChange(next ?? '')}
      shouldSearchOnDebounce={1000}
      shouldSearchOnClear
      size="sm"
    />
  );
}

interface NameFilterProps {
  type: string;
  name: string;
  sentEvents: string[];
  sentAttributes: string[];
  onChange: (name: string) => void;
}

export function NameFilter({ type, name, sentEvents, sentAttributes, onChange }: NameFilterProps) {
  if (isEmptyString(type)) return null;

  if (FREE_TEXT_TYPES.includes(type)) {
    return <FreeTextNameFilter label={nameLabel(type)} name={name} onChange={onChange} />;
  }

  const options = nameOptions(type, sentEvents, sentAttributes);
  if (options.length === 0) return null;

  return (
    <Select
      label={nameLabel(type)}
      placeholder="any"
      value={isEmptyString(name) ? undefined : name}
      onChange={(value) => onChange(value ?? '')}
      options={options.map((option) => ({ value: option, label: option }))}
      isSearchable
      isClearable
      size="sm"
    />
  );
}
